import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { BarChart3, Users, Clock, Calendar, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

const Analytics = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics`, { withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Failed to load analytics");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-zinc-500 dark:text-zinc-400">No analytics data available</p>
        </div>
      </Layout>
    );
  }

  const maxHours = Math.max(...data.employee_analytics.map(e => e.total_hours), 1);
  const maxShiftCount = Math.max(...(data.coverage_by_shift.map(s => s.count) || [1]), 1);

  return (
    <Layout>
      <div className="space-y-6" data-testid="analytics-dashboard">
        {/* Header */}
        <div>
          <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Analytics</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Workforce insights across all rosters
          </p>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-200 border border-zinc-200 dark:border-zinc-800">
          {[
            { label: "Total Rosters", value: data.total_rosters, icon: Calendar },
            { label: "Employees", value: data.total_employees, icon: Users },
            { label: "Assignments", value: data.total_entries, icon: Clock },
            { label: "Days Covered", value: data.dates_covered, icon: TrendingUp },
            { label: "Avg Staff/Day", value: data.avg_staff_per_day, icon: BarChart3 },
          ].map((stat, idx) => {
            const Icon = stat.icon;
            return (
              <div key={idx} className="bg-white dark:bg-zinc-900 p-5">
                <Icon className="w-4 h-4 text-zinc-400 dark:text-zinc-500 mb-3" />
                <p className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-100">{stat.value}</p>
                <p className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mt-1">
                  {stat.label}
                </p>
              </div>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Hours Per Employee */}
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800">
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="text-lg tracking-tight font-medium text-zinc-900 dark:text-zinc-100">Hours Per Employee</h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Total scheduled hours across all rosters</p>
            </div>
            <div className="p-4 space-y-3">
              {data.employee_analytics.length === 0 ? (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-6">No data yet</p>
              ) : (
                data.employee_analytics.map((emp) => (
                  <div key={emp.employee_id} className="flex items-center gap-3" data-testid={`analytics-emp-${emp.employee_id}`}>
                    <Avatar className="w-7 h-7 border border-zinc-200 dark:border-zinc-800 flex-shrink-0">
                      <AvatarFallback className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 text-xs">
                        {getInitials(emp.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{emp.name}</span>
                        <span className="text-xs tabular-nums text-zinc-600 dark:text-zinc-400 ml-2 flex-shrink-0">
                          {emp.total_hours}h / {emp.max_hours}h
                        </span>
                      </div>
                      <div className="w-full bg-zinc-100 dark:bg-zinc-800 h-2 rounded-sm overflow-hidden">
                        <div
                          className="h-full rounded-sm transition-all"
                          style={{
                            width: `${Math.min((emp.total_hours / maxHours) * 100, 100)}%`,
                            backgroundColor: emp.utilization > 100 ? '#FF3B30' : '#002FA7'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Shift Coverage */}
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800">
            <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
              <h2 className="text-lg tracking-tight font-medium text-zinc-900 dark:text-zinc-100">Shift Coverage</h2>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">Distribution of assignments by shift type</p>
            </div>
            <div className="p-4 space-y-4">
              {data.coverage_by_shift.length === 0 ? (
                <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center py-6">No data yet</p>
              ) : (
                data.coverage_by_shift.map((shift, idx) => (
                  <div key={idx} data-testid={`analytics-shift-${shift.name}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-sm"
                          style={{ backgroundColor: shift.color }}
                        />
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{shift.name}</span>
                      </div>
                      <span className="text-sm tabular-nums font-medium text-zinc-600 dark:text-zinc-400">
                        {shift.count} assignments
                      </span>
                    </div>
                    <div className="w-full bg-zinc-100 dark:bg-zinc-800 h-3 rounded-sm overflow-hidden">
                      <div
                        className="h-full rounded-sm transition-all"
                        style={{
                          width: `${(shift.count / maxShiftCount) * 100}%`,
                          backgroundColor: shift.color
                        }}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Detailed Employee Table */}
        <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 overflow-hidden">
          <div className="p-4 border-b border-zinc-200 dark:border-zinc-800">
            <h2 className="text-lg tracking-tight font-medium text-zinc-900 dark:text-zinc-100">Employee Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-left py-3 px-4">Employee</th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-right py-3 px-4">Total Hours</th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-right py-3 px-4">Shifts</th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-right py-3 px-4">Weekend Shifts</th>
                  <th className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 text-right py-3 px-4">Utilization</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {data.employee_analytics.map((emp) => (
                  <tr key={emp.employee_id} className="hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors">
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Avatar className="w-6 h-6 border border-zinc-200 dark:border-zinc-800">
                          <AvatarFallback className="bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 text-xs">
                            {getInitials(emp.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{emp.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm tabular-nums text-zinc-900 dark:text-zinc-100">{emp.total_hours}h</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm tabular-nums text-zinc-900 dark:text-zinc-100">{emp.shift_count}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className="text-sm tabular-nums text-zinc-900 dark:text-zinc-100">{emp.weekend_shifts}</span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <span className={`
                        text-sm tabular-nums font-medium px-2 py-0.5 rounded
                        ${emp.utilization > 100 ? 'bg-red-50 text-red-700' : 
                          emp.utilization > 80 ? 'bg-amber-50 text-amber-700' : 
                          'bg-emerald-50 text-emerald-700'}
                      `}>
                        {emp.utilization}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Analytics;
